import { StoryProgress, TutorialFacts } from "./GameState";
import { MachineState } from "./Machine";
import { MaterialInstance, Species } from "./Materials";
import { ProductPlanId } from "./plans";
import { ownsTool } from "./progression-helpers";
import { hasSkill } from "./skill-helpers";
import { isNight } from "./time";
import {
  FIRST_MACHINE_IDS,
  hasShelf,
  hasShelfParts,
  needsFirstPallet,
  palletHome,
  palletOnBench,
  shelfLaidOut,
  shopMaterials,
  soldFirstPiece,
  TUTORIAL_MONEY_GOAL,
} from "./tutorial-goal";

/**
 * The story: the wife and her phone.
 *
 * She finds things online at stupid prices; the character thinks "I
 * could make that"; making them is what builds the shop. The opening is
 * told in three kinds of machinery, all data in this module:
 *
 * - **Wants** (`WANTS`) are far-off goals. A want is a real plan the
 *   player cannot build yet, shown as a listing on her phone. The
 *   current want is the shop's horizon until she moves on to the next
 *   (`currentWant`; nothing pins it on screen — a log of the wants is
 *   issue #247). They form a capability ladder (a pallet and bare
 *   hands; real lumber and a saw; box joinery and a planer), so an hour
 *   of other work sits between each one. The finished piece always
 *   sells — the stand is the game's one selling channel — and each
 *   scene gives a different reason why: she has seen the piece and
 *   wants the next thing *instead*.
 * - **Scenes** (`STORY_SCENES`) are the story surface: a Pokémon-style
 *   cutscene — she walks in from off screen, walks up to the finished
 *   piece, a dialogue box runs line by line, her phone fills the screen
 *   with the next listing, she leaves — or, for the smaller beats, just
 *   the character's own thought in the same box. A scene has a one-way,
 *   cumulative `due` predicate over the shop (like a tutorial step's),
 *   plays once, and is recorded in the save, so a reload never replays
 *   one. Playing stops the clock and takes the keys (the StoryTracker's
 *   TimeFlow stop; the shell's CutsceneDirector performs the beats).
 *   Through the opening he thinks something at every step — the pallet
 *   found, the pallet home, the pallet on the bench, the boards freed,
 *   the boards laid out, the shelf done — one thought scene per box on
 *   the card. One of them is thought from the cab (`fromTheCab`): the
 *   only scene that plays with the player away from the floor.
 * - **Where a scene plays**: a thought is had wherever the character
 *   is, leaned over the bench included — the bench steps are worked in
 *   the bench view, and their thoughts come up over it the moment the
 *   work is done. A scene she walks into (`sceneNeedsTheFloor`) waits
 *   for the player to stand back up: she walks up to the piece, and the
 *   view has to be on the floor for that. So a finished piece gets its
 *   verdict at the bench and her visit when he stands (the shelf's is
 *   `shelfDone`, then `shelfBuilt`).
 * - **Reveals**: a tutorial step names the scene that reveals it
 *   (`TutorialStep.revealedBy`), so the to-do card grows one thought at
 *   a time. The ratchet underneath is untouched — a hidden step the
 *   player satisfies early is ticked the moment its thought arrives.
 *
 * Voice: her lines wear the "Wife" nameplate and speak plainly; his
 * thoughts wear no nameplate and the handwriting face. This is the one
 * place outside the manual's margin Notes that personality is allowed
 * (docs/shop-manual.md, voice rule 5).
 *
 * The presentation — the walk, the letterbox, the typewriter, the phone
 * card — lives in `src/shell/scenes/story/` and `src/shell/hud/story/`;
 * the tracker and commands in `src/sim/`. This module is pure: it
 * answers which scene is due and which want is pinned, from facts.
 */

export const WANT_IDS = ["rusticShelf", "sideTable", "jewelryBox"] as const;
export type WantId = (typeof WANT_IDS)[number];

/** One listing on her phone. */
export interface Want {
  readonly id: WantId;
  /** The finished piece that satisfies it — a plan's product type. */
  readonly productType: ProductPlanId;
  /** The listing as the website wrote it. */
  readonly listingTitle: string;
  /** The website's price, in dollars. Stupidly high on purpose. */
  readonly listingPrice: number;
  /** The listing's sales pitch, a line under the title. */
  readonly listingBlurb: string;
  /** The wood the listing's product photo shows. */
  readonly photoSpecies: Species;
}

export const WANTS: ReadonlyArray<Want> = [
  {
    id: "rusticShelf",
    productType: "rusticShelf",
    listingTitle: "Rustic Reclaimed-Wood Wall Shelf",
    listingPrice: 150,
    listingBlurb: "Hand-built from authentic salvaged pallet boards.",
    photoSpecies: "pallet",
  },
  {
    id: "sideTable",
    productType: "sideTable",
    listingTitle: "Solid Hardwood Side Table",
    listingPrice: 620,
    listingBlurb: "Glued-up top, four square legs. Heirloom quality.",
    photoSpecies: "oak",
  },
  {
    id: "jewelryBox",
    productType: "jewelryBox",
    listingTitle: "Artisan Jewelry Box",
    listingPrice: 340,
    listingBlurb: "Thin sanded hardwood with a fitted divider.",
    photoSpecies: "walnut",
  },
];

export function wantById(id: WantId): Want {
  const want = WANTS.find((candidate) => candidate.id === id);
  if (!want) throw new Error(`Unknown want: ${id}`);
  return want;
}

// ---------------------------------------------------------------------------
// Scenes
// ---------------------------------------------------------------------------

export const STORY_SCENE_IDS = [
  "opener",
  "palletFound",
  "palletHome",
  "palletOnBench",
  "boardsFreed",
  "partsPlaced",
  "shelfDone",
  "shelfBuilt",
  "firstSale",
  "skillLearned",
  "sawHome",
  "sawMounted",
  "birdhouseBuilt",
  "savingsReached",
  "firstNight",
  "floorDusty",
  "sideTableBuilt",
  "jewelryBoxBuilt",
] as const;
export type StorySceneId = (typeof STORY_SCENE_IDS)[number];

/** One line of a scene's script. */
export type StoryBeat =
  /** She walks in from off screen, up to the scene's subject. */
  | { readonly kind: "enter" }
  /** She says a line. */
  | { readonly kind: "say"; readonly text: string }
  /** She says a line while holding up her phone with a listing on it. */
  | { readonly kind: "phone"; readonly want: WantId; readonly text: string }
  /** She walks back out the way she came. */
  | { readonly kind: "leave" }
  /** The character's own thought, in his own hand. */
  | { readonly kind: "think"; readonly text: string };

export interface StoryScene {
  readonly id: StorySceneId;
  /**
   * When the scene is owed. Written cumulatively, like a tutorial
   * step's predicate: once true it stays true however the shop changes
   * after, so a scene that came due can't slip away before it plays.
   */
  readonly due: (facts: TutorialFacts) => boolean;
  readonly beats: ReadonlyArray<StoryBeat>;
  /**
   * The piece she walks up to — the want this scene answers. Absent,
   * she walks to the player (the opener), or nobody walks at all.
   */
  readonly subject?: WantId;
  /**
   * A thought had in the truck's cab, out scavenging: the one place
   * off the floor a scene may play, and only while the trip is parked
   * at its keep-searching-or-head-home decision. Thoughts only — she
   * can't walk into a cab.
   */
  readonly fromTheCab?: true;
}

const say = (text: string): StoryBeat => ({ kind: "say", text });
const think = (text: string): StoryBeat => ({ kind: "think", text });
const phone = (want: WantId, text: string): StoryBeat => ({
  kind: "phone",
  want,
  text,
});
const ENTER: StoryBeat = { kind: "enter" };
const LEAVE: StoryBeat = { kind: "leave" };

/** Whether a want's piece exists anywhere in the shop right now. */
export function wantPieceExists(facts: TutorialFacts, wantId: WantId): boolean {
  const type = wantById(wantId).productType;
  return shopMaterials(facts).some((material) => material.type === type);
}

/** The index in WANTS the shop has reached: the latest want whose
 * reveal scene has played, or -1 before the opener. */
function wantIndexReached(story: StoryProgress): number {
  let reached = -1;
  for (const scene of STORY_SCENES) {
    if (!story.scenesPlayed.includes(scene.id)) continue;
    for (const beat of scene.beats) {
      if (beat.kind === "phone") {
        reached = Math.max(reached, WANT_IDS.indexOf(beat.want));
      }
    }
  }
  return reached;
}

/**
 * Whether the story has moved past a want: the scene she comes to see
 * its piece in has played. Cumulative on purpose — a later want's scene
 * counts too, since the script only reaches it past this one.
 */
function wantAnswered(story: StoryProgress, wantId: WantId): boolean {
  const index = WANT_IDS.indexOf(wantId);
  return STORY_SCENES.some(
    (scene) =>
      scene.subject !== undefined &&
      WANT_IDS.indexOf(scene.subject) >= index &&
      story.scenesPlayed.includes(scene.id),
  );
}

const isPurchasedMachine = (machine: MachineState): boolean =>
  FIRST_MACHINE_IDS.includes(machine.machineTypeId);

/** A machine bought for the shop, anywhere: crated, carried, or standing. */
function ownsBoughtMachine(facts: TutorialFacts): boolean {
  return (
    facts.machines.some(isPurchasedMachine) ||
    facts.machineCrates.some((crate) => isPurchasedMachine(crate.machine)) ||
    facts.truck.crates.some(isPurchasedMachine) ||
    (facts.player.carriedMachine != null &&
      isPurchasedMachine(facts.player.carriedMachine))
  );
}

const reachedMoneyGoal = (facts: TutorialFacts) =>
  facts.money >= TUTORIAL_MONEY_GOAL;

/** The shelf exists, or did: cumulative through its sale and her visit. */
const shelfExistsOrSold = (facts: TutorialFacts) =>
  wantPieceExists(facts, "rusticShelf") ||
  facts.progression.salesCompleted > 0 ||
  facts.progression.standUnlocked ||
  wantAnswered(facts.progression.story, "rusticShelf");

export const STORY_SCENES: ReadonlyArray<StoryScene> = [
  {
    // A new game opens on this: he's in the garage, she walks in with
    // the listing. Always owed until it has played.
    id: "opener",
    due: () => true,
    beats: [
      ENTER,
      say("Hey. Look what I found."),
      phone(
        "rusticShelf",
        "Isn't it perfect? Reclaimed wood. It'd go right over the desk.",
      ),
      think(
        "A hundred and fifty dollars. For six pallet boards and a handful of nails.",
      ),
      think("I could make that."),
      say(
        "You've got that look. Fine. Make me one, then. I'll hold off on ordering it.",
      ),
      LEAVE,
      think(
        "First I need wood. There are always pallets stacked out behind the stores.",
      ),
      think("The truck's out front."),
    ],
  },
  // The first build, thought through one step at a time. Each of these
  // is one thought scene per box on the to-do card: it comes due the
  // moment the previous box is satisfied, and its thought reveals the
  // next one. Every predicate is cumulative through the shelf's sale,
  // so a player who outruns the thoughts gets them in order, back to
  // back, rather than never.
  {
    // Thought from the cab, parked at the stop where the pallet turned
    // up — the trip's own screen is up, the shop is behind it.
    id: "palletFound",
    fromTheCab: true,
    due: (facts) => !needsFirstPallet(facts),
    beats: [
      think("There's one. Solid stringers, all the boards on it."),
      think("Good enough. Back to the shop."),
    ],
  },
  {
    id: "palletHome",
    due: (facts) =>
      palletHome(facts) ||
      palletOnBench(facts) ||
      hasShelfParts(facts) ||
      hasShelf(facts) ||
      soldFirstPiece(facts),
    beats: [think("It's in the bed. Get it out and onto the workbench.")],
  },
  {
    id: "palletOnBench",
    due: (facts) =>
      palletOnBench(facts) ||
      hasShelfParts(facts) ||
      hasShelf(facts) ||
      soldFirstPiece(facts),
    beats: [
      think("Two nails per board. The claw on the hammer will pull them."),
    ],
  },
  {
    id: "boardsFreed",
    due: (facts) =>
      hasShelfParts(facts) || hasShelf(facts) || soldFirstPiece(facts),
    beats: [
      think("Six boards. That's the whole shelf, lying right there."),
      think(
        "The plan's in the drawer under the bench. Lay each board where the drawing says.",
      ),
    ],
  },
  {
    id: "partsPlaced",
    due: (facts) =>
      shelfLaidOut(facts) || hasShelf(facts) || soldFirstPiece(facts),
    beats: [think("Every piece where it goes. Now nail it together.")],
  },
  {
    // The finished shelf: his verdict, thought at the bench where the
    // last nail went in. Her visit is its own scene, because she walks
    // in, and that waits for him to stand up.
    id: "shelfDone",
    due: shelfExistsOrSold,
    beats: [
      think("There. Done."),
      think("It looks like the picture. It looks better than the picture."),
    ],
  },
  {
    // She comes to see the shelf, and wants the next thing *instead* —
    // the shelf is his to sell — which is what makes the stand the
    // answer to a piece made for her.
    id: "shelfBuilt",
    subject: "rusticShelf",
    due: shelfExistsOrSold,
    beats: [
      ENTER,
      say("Is that it? Let me see."),
      say("That's... actually really nice. Better than the picture."),
      say(
        "Okay, don't get mad. I was looking again, and I don't think a shelf is what that wall needs.",
      ),
      phone(
        "sideTable",
        "This. Next to the couch. Real wood, real legs. I want this instead.",
      ),
      say(
        "Sell the shelf. You did it in a day, and somebody will want it. Then make me this one.",
      ),
      LEAVE,
      think("Instead. Fine."),
      think("Fancy-rustic, actual-rustic. Somebody would pay for it."),
      think(
        "A table at the end of the driveway and a sign. People walk past all day.",
      ),
      think(
        "And that side table takes real lumber and real tools. Tools cost money. So: sell the shelf.",
      ),
    ],
  },
  {
    id: "firstSale",
    due: (facts) => facts.progression.salesCompleted > 0,
    beats: [
      think("Somebody actually bought it."),
      think(
        "I learned a thing or two building that. Worth writing down in the journal.",
      ),
    ],
  },
  {
    id: "skillLearned",
    due: (facts) =>
      hasSkill(facts.progression, "rusticProjects") ||
      ownsTool(facts, "handSaw"),
    beats: [
      think(
        "A birdhouse would sell. I'd need to cut boards to length for it, though.",
      ),
      think(
        "A hand saw. The Orange Box will have one, and the shelf money covers it.",
      ),
    ],
  },
  {
    id: "sawHome",
    due: (facts) =>
      (ownsTool(facts, "handSaw") && facts.player.away === null) ||
      facts.machines.some((machine) => machine.tools.includes("handSaw")),
    beats: [think("Let's hang this on the bench.")],
  },
  {
    id: "sawMounted",
    due: (facts) =>
      facts.machines.some((machine) => machine.tools.includes("handSaw")) ||
      wantPieceExistsByType(facts, "birdhouse"),
    beats: [
      think("Now the birdhouse. Cut the parts to length, then nail it up."),
    ],
  },
  {
    id: "birdhouseBuilt",
    due: (facts) =>
      wantPieceExistsByType(facts, "birdhouse") || reachedMoneyGoal(facts),
    beats: [
      think("Now to make this pay. Keep building, keep setting work out."),
      think("Three hundred dollars would buy a real saw."),
    ],
  },
  {
    id: "savingsReached",
    due: (facts) => reachedMoneyGoal(facts) || ownsBoughtMachine(facts),
    beats: [
      think("Three hundred. That's a miter saw, with some left over."),
      think(
        "It comes home crated in the truck. I'll have to carry it in and find it a spot.",
      ),
    ],
  },
  {
    id: "firstNight",
    due: (facts) =>
      facts.day > 1 || isNight(facts) || facts.player.away?.kind === "home",
    beats: [think("It's dark. Call it a day. Home's a short drive.")],
  },
  {
    id: "floorDusty",
    due: (facts) => facts.progression.sweepingUnlocked,
    beats: [think("This place is a mess.")],
  },
  {
    id: "sideTableBuilt",
    subject: "sideTable",
    due: (facts) =>
      wantPieceExists(facts, "sideTable") ||
      wantAnswered(facts.progression.story, "sideTable"),
    beats: [
      ENTER,
      say("Oh. Oh, that's real furniture."),
      say(
        "You know what I noticed? Every time you finish something, it's twice as good as the last thing.",
      ),
      phone(
        "jewelryBox",
        "So I've been thinking small. A little box. With the fancy corners.",
      ),
      say(
        "Sell the table. A real shop needs a real planer, and that's not coming out of the grocery money.",
      ),
      LEAVE,
      think("A jewelry box. Thin, sanded stock. That's a planer's job."),
    ],
  },
  {
    id: "jewelryBoxBuilt",
    subject: "jewelryBox",
    due: (facts) => wantPieceExists(facts, "jewelryBox"),
    beats: [
      ENTER,
      say("Look at those corners. Look at them."),
      say("I'm keeping this one. No. I'm keeping this one."),
      say("...Okay, sell it. But the next one's mine."),
      say("And don't get any ideas about a boat. I saw the magazine."),
      LEAVE,
      think("A boat. Ha."),
      think("...Someday."),
    ],
  },
];

function wantPieceExistsByType(
  facts: TutorialFacts,
  type: MaterialInstance["type"],
): boolean {
  return shopMaterials(facts).some((material) => material.type === type);
}

export function sceneById(id: StorySceneId): StoryScene {
  const scene = STORY_SCENES.find((candidate) => candidate.id === id);
  if (!scene) throw new Error(`Unknown story scene: ${id}`);
  return scene;
}

export function scenePlayed(story: StoryProgress, id: StorySceneId): boolean {
  return story.scenesPlayed.includes(id);
}

/**
 * Whether the scene needs the player standing on the shop floor: she
 * walks in, and she walks up to a piece or to him, so the view has to
 * be the floor's. A scene of thoughts alone plays wherever he is —
 * leaned over the bench included, where most of the first build's
 * thoughts are owed the moment the work under the hand is done.
 */
export function sceneNeedsTheFloor(scene: StoryScene): boolean {
  return scene.beats.some((beat) => beat.kind === "enter");
}

/**
 * The scene owed right now: the first in script order that hasn't
 * played and whose condition holds, while the player is on the floor —
 * not away on a trip and not carrying a machine, since she walks up to
 * him and the box takes the keys. The one exception is a thought
 * marked `fromTheCab`, owed out scavenging while the trip sits at its
 * decision. Null when nothing is owed. The shell adds its own
 * conditions (the truck parked, no dialog up, and the floor for a
 * scene that `sceneNeedsTheFloor`) before it begins one; the driver
 * begins one whenever this says so.
 */
export function dueScene(facts: TutorialFacts): StoryScene | null {
  if (facts.player.carriedMachine != null) return null;
  const away = facts.player.away;
  const inTheCab =
    away?.kind === "scavenging" && away.phase.kind === "deciding";
  for (const scene of STORY_SCENES) {
    if (scenePlayed(facts.progression.story, scene.id)) continue;
    if (away !== null && !(inTheCab && scene.fromTheCab)) continue;
    if (scene.due(facts)) return scene;
  }
  return null;
}

/**
 * The shop's horizon: the latest want she has shown and he hasn't
 * answered yet. Null before the opener and after the last want.
 */
export function currentWant(story: StoryProgress): Want | null {
  const reached = wantIndexReached(story);
  if (reached < 0) return null;
  const want = WANTS[reached];
  return wantAnswered(story, want.id) ? null : want;
}
